import { RISK_ASSESSMENT } from '@/constants';
import { faker } from '@faker-js/faker';

const mockIndividualUser = ({ hasWalletAssociated = false } = {}) => {
  const item = {
    id: faker.string.uuid(),
    firstname: faker.person.firstName(),
    lastname: faker.person.lastName(),
    created_at: faker.date.past(),
    verified_at: null,
    verified_by: null,
    verified_user_id: null,
    approval_key: faker.word.noun(),
    approval_state_description: faker.word.noun(),
    approval_note: null,
    gender: faker.word.noun(),
    date_of_birth: faker.date.past(),
    place_of_birth: faker.word.noun(),
    citizenship: faker.word.noun(),
    tax_id: faker.word.noun(),
    user_hw_wallet_address: `did:com:1${faker.string.alphanumeric({ length: 39 })}`,
    wallets: [
      {
        id: faker.string.uuid(),
        wallet: `did:com:1${faker.string.alphanumeric({ length: 39 })}`,
        is_individual_wallet: hasWalletAssociated,
      },
    ],
  };
  return item;
};

const mockQuestionnaireItem = () => {
  const item = {
    question: faker.lorem.sentence(),
    answer: faker.lorem.sentence(),
  };
  return item;
};

const mockIndividualQuestionnaire = () => {
  const item = {
    id: faker.string.uuid(),
    questionnaire: JSON.stringify({
      questionnaire_version: faker.word.noun(),
      questionnaire_data: faker.date.past(),
      elements: faker.helpers.multiple(() => mockQuestionnaireItem(), {
        count: 2,
      }),
    }),
    created_at: faker.date.past(),
  };
  return item;
};

const mockCurrentQuestionnaireItem = () => {
  const item = {
    id: faker.string.uuid(),
    questionnaire_id: faker.string.uuid(),
    question_external_id: faker.word.noun(),
    question: faker.lorem.sentence(),
    answer: faker.lorem.sentence(),
    risk_score: faker.number.int({ min: 1, max: 10 }),
    weight_percent: faker.number.float({ min: 0, max: 1 }),
    display_order: faker.number.int({ min: 1, max: 10 }),
    is_questionnaire_question: faker.datatype.boolean(),
    handled_at: faker.date.past(),
    handled_by: faker.string.uuid(),
    handled_by_user: faker.person.fullName(),
  };
  return item;
};

const mockIndividualCurrentQuestionnaire = () => {
  const firstname = faker.person.firstName();
  const lastname = faker.person.lastName();
  const item = {
    questionnaire_master_data: {
      id: faker.string.uuid(),
      individual_id: faker.string.uuid(),
      fullname: `${firstname} ${lastname}`,
      firstname,
      lastname,
      address: faker.location.streetAddress(),
      province: faker.location.county(),
      city: faker.location.city(),
      state: faker.location.state(),
      email: faker.internet.email({
        firstName: firstname,
        lastName: lastname,
      }),
      gender: faker.person.sex(),
      date_of_birth: faker.date.past(),
      place_of_birth: faker.location.city(),
      citizenship: faker.location.state(),
      wallet: `did:com:1${faker.string.alphanumeric({ length: 39 })}`,
      version: faker.word.noun(),
      questionnaire_date: faker.date.past(),
      valid_date_from: faker.date.past(),
      valid_date_to: faker.date.past(),
      risk_level_id: faker.string.uuid(),
      risk_level_key: faker.word.noun(),
      severity_level: faker.number.int({ min: 1, max: 10 }),
      severity_value: faker.number.int({ min: 1, max: 10 }),
      approval_state_id: faker.string.uuid(),
      approval_state_key: faker.word.noun(),
      cdd: faker.word.noun(),
      edd: faker.word.noun(),
      cdd_date: faker.date.past(),
      edd_date: faker.date.past(),
      cdd_state_id: faker.string.uuid(),
      edd_state_id: faker.string.uuid(),
      cdd_state_description: faker.lorem.sentence(),
      edd_state_description: faker.lorem.sentence(),
      handled_at: faker.date.past(),
      handled_by: faker.string.uuid(),
      handled_by_user: faker.person.fullName(),
      created_on: faker.date.past(),
    },
    questionnaire_details_data: faker.helpers.multiple(
      () => mockCurrentQuestionnaireItem(),
      { count: 2 },
    ),
  };
  return item;
};

const mockIndividualDocument = () => {
  const item = {
    document_id: faker.string.uuid(),
    document_type_key: faker.word.noun(),
    file_type_key: faker.word.noun(),
    is_valid: faker.datatype.boolean(),
    verified_at: faker.date.past(),
    verified_by: faker.string.uuid(),
    document_number: faker.string.alphanumeric(),
    valid_from: faker.date.past(),
    valid_until: faker.date.future(),
    issue_date: faker.date.past(),
    authority_issuing: faker.word.noun(),
    file_stream: '',
    file_uri: faker.internet.url(),
    file_name: `${faker.word.noun()}.jpg`,
  };
  return item;
};

const mockIndividualDocuments = (count = 3) =>
  faker.helpers.multiple(() => mockIndividualDocument(), { count });

const mockIndividualVideo = () => {
  const item = {
    video_id: faker.string.uuid(),
    is_valid_video: faker.datatype.boolean(),
    number_to_verified: faker.string.numeric(),
    file_uri_video: faker.internet.url(),
    file_name_video: `${faker.word.noun()}.mp4`,
    file_name_video_original: `${faker.word.noun()}.mp4`,
    verified_video_at: faker.date.past(),
    verified_video_by: faker.string.uuid(),
    is_valid_document: faker.datatype.boolean(),
    verified_document_at: faker.date.past(),
    verified_document_by: faker.string.uuid(),
  };
  return item;
};

const mockIndividualDilisense = () => {
  const item = {
    id: faker.string.uuid(),
    is_valid: faker.datatype.boolean(),
    verified_at: faker.date.past(),
    verified_by: faker.string.uuid(),
    list_file_uri: [
      {
        document_id: faker.string.uuid(),
        file_uri: faker.internet.url(),
        file_name: `${faker.word.noun()}.pdf`,
        file_size: faker.number.int(),
        file_hash: faker.string.alphanumeric(64),
        file_name_original: `${faker.word.noun()}.pdf`,
        uploaded_at: faker.date.past(),
      },
    ],
  };
  return item;
};

const mockIndividualAccount = ({ hasWalletAssociated = false } = {}) => {
  const item = {
    individual_master_data: mockIndividualUser({ hasWalletAssociated }),
    individual_documents: mockIndividualDocuments(),
    individual_video: mockIndividualVideo(),
    individual_questionnaire: {
      id: faker.string.uuid(),
      questionnaire:
        '[{"id":"63db7b22-9d50-4be0-a097-305580d25e8e","question":"Why did you open the account?","answer":"Personal Savings","risk":1},{"id":"d07acb33-2815-44e8-be95-fefee8667140","question":"What is your job?","answer":"Employee (private sector)","risk":1}]',
      created_at: faker.date.past(),
    },
  };
  return item;
};

const mockIndividualRiskAsssessmentLevel = () => {
  const item = {
    id: faker.string.uuid(),
    position: faker.number.int({ min: 1, max: 10 }),
    level_key: faker.word.noun(),
    short_description: faker.lorem.sentence({ min: 3, max: 5 }),
    long_description: faker.lorem.sentence({ min: 5, max: 15 }),
    severity: faker.number.int({ min: 1, max: 10 }),
    archived_by: faker.person.fullName(),
    archived_at: faker.date.past(),
  };
  return item;
};

const mockIndividualRiskAsssessmentLevels = (count = 3) =>
  faker.helpers.multiple(() => mockIndividualRiskAsssessmentLevel(), { count });

const mockIndividualRiskAsssessmentApprovalState = () => {
  const item = {
    id: faker.string.uuid(),
    approval_key: faker.word.noun(),
    description: faker.lorem.sentence({ min: 3, max: 5 }),
  };
  return item;
};

const mockIndividualRiskAsssessmentApprovalStates = (count = 3) =>
  faker.helpers.multiple(() => mockIndividualRiskAsssessmentApprovalState(), {
    count,
  });

const mockIndividualRiskAsssessmentUser = () => {
  const firstname = faker.person.firstName();
  const lastname = faker.person.lastName();
  const item = {
    individual_id: faker.string.uuid(),
    fullname: `${firstname} ${lastname}`,
    firstname,
    lastname,
    address: faker.location.streetAddress(),
    province: faker.location.county(),
    city: faker.location.city(),
    state: faker.location.state(),
    email: faker.internet.email({
      firstName: firstname,
      lastName: lastname,
    }),
    gender: faker.person.sex(),
    date_of_birth: faker.date.past(),
    place_of_birth: faker.location.city(),
    citizenship: faker.location.state(),
    wallet: `did:com:1${faker.string.alphanumeric({ length: 39 })}`,
    handled_at: faker.date.past(),
    handled_by: faker.string.uuid(),
    handled_by_user: faker.person.fullName(),
    created_on: faker.date.past(),
    risk_level_id: faker.string.uuid(),
    risk_level_key: faker.word.noun(),
    risk_level_description: faker.lorem.sentence({ min: 3, max: 5 }),
    severity_level: faker.number.int({ min: 1, max: 10 }),
    severity_value: faker.number.int({ min: 1, max: 10 }),
    approval_state_id: faker.string.uuid(),
    approval_state_key: faker.word.noun(),
    approval_state_description: faker.lorem.sentence({ min: 3, max: 5 }),
  };
  return item;
};

const mockIndividualRiskAsssessmentUsers = (count = 3) =>
  faker.helpers.multiple(() => mockIndividualRiskAsssessmentUser(), { count });

const mockIndividualRiskAssessmentSummary = () => {
  const item = {
    unassigned: faker.number.int({ min: 1, max: 10 }),
    low: faker.number.int({ min: 1, max: 10 }),
    medium: faker.number.int({ min: 1, max: 10 }),
    high: faker.number.int({ min: 1, max: 10 }),
    severe: faker.number.int({ min: 1, max: 10 }),
  };
  return item;
};

const mockIndividualCddEddState = (key) => {
  const item = {
    id: faker.string.uuid(),
    state_key: key,
    description: key.toLowerCase(),
  };
  return item;
};

const mockIndividualCddEddStates = () => {
  const states = Object.values(RISK_ASSESSMENT.DUE_DILIGENCE_STATUS);
  return states.map((state) => mockIndividualCddEddState(state));
};

const mockIndividualRiskAssessmentState = (key) => {
  const type = faker.word.noun();
  const id = faker.string.uuid();
  const item = {
    type,
    state_id: id,
    state_key: key,
    state_description: `${type} ${key.toLowerCase()}`,
    filter_key: `${type}:${id}`,
  };
  return item;
};

const mockIndividualRiskAssessmentStates = () => {
  const states = Object.values(RISK_ASSESSMENT.DUE_DILIGENCE_STATUS);
  return states.map((state) => mockIndividualRiskAssessmentState(state));
};

export {
  mockIndividualAccount,
  mockIndividualCddEddState,
  mockIndividualCddEddStates,
  mockIndividualCurrentQuestionnaire,
  mockIndividualDilisense,
  mockIndividualDocument,
  mockIndividualDocuments,
  mockIndividualQuestionnaire,
  mockIndividualRiskAssessmentState,
  mockIndividualRiskAssessmentStates,
  mockIndividualRiskAssessmentSummary,
  mockIndividualRiskAsssessmentApprovalState,
  mockIndividualRiskAsssessmentApprovalStates,
  mockIndividualRiskAsssessmentLevel,
  mockIndividualRiskAsssessmentLevels,
  mockIndividualRiskAsssessmentUser,
  mockIndividualRiskAsssessmentUsers,
  mockIndividualUser,
  mockIndividualVideo,
};
